import React from "react";
import { alpha, useTheme } from "@mui/material/styles";
import { Box, Button, Card, CardContent, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { CalendarMonth, DirectionsCar, Euro, PersonOutline, Search } from "@mui/icons-material";
import { durations, fieldSpacing, provinceOptions } from "../constants";

function DealerQuoteForm({ formData, errors, handleFieldChange, handleDurationSelect, handleSubmit }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const ui = {
    mutedText: theme.palette.text.secondary,
    headingText: theme.palette.text.primary,
    cardBorder: alpha(theme.palette.divider, isDark ? 0.45 : 0.9),
    cardShadow: isDark ? "0 14px 34px rgba(0,0,0,0.38)" : "0 14px 34px rgba(15,23,42,0.08)",
    inputBg: isDark ? alpha(theme.palette.background.default, 0.5) : alpha(theme.palette.background.paper, 0.92),
    chipInactiveBg: isDark ? alpha(theme.palette.common.white, 0.08) : alpha(theme.palette.text.primary, 0.08),
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      bgcolor: ui.inputBg,
      "&.Mui-error .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.error.main,
        borderWidth: 1.5,
      },
      "&.Mui-error:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.error.main,
      },
      "&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.error.main,
      },
    },
  };

  const provinceMenuProps = {
    PaperProps: {
      sx: {
        maxHeight: { xs: 280, sm: 360 },
        mt: 0.5,
        border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
        bgcolor: theme.palette.background.paper,
        boxShadow: isDark
          ? "0 16px 34px rgba(0,0,0,0.45)"
          : "0 16px 34px rgba(15,23,42,0.16)",
      },
    },
    MenuListProps: {
      dense: true,
      sx: {
        py: 0.5,
      },
    },
  };

  return (
    <Card
      sx={{
        borderRadius: 4,
        border: `1px solid ${ui.cardBorder}`,
        boxShadow: ui.cardShadow,
        bgcolor: theme.palette.background.paper,
      }}
    >
      <CardContent sx={{ p: 0 }}>
        <Box component="form" noValidate onSubmit={handleSubmit} sx={{ p: { xs: 3.5, md: 4.5 } }}>
          <Typography variant="h3" fontWeight={800} color={ui.headingText} mb={fieldSpacing}>
            Dati Preventivo
          </Typography>

          <Typography fontWeight={800} mb={1.2} color={ui.headingText}>
            <Search sx={{ mr: 1, verticalAlign: "middle", fontSize: 20, color: ui.mutedText }} />
            Targa veicolo <Typography component="span" color={ui.mutedText}>(opzionale)</Typography>
          </Typography>
          <TextField
            fullWidth
            placeholder="ES. AB123CD"
            value={formData.targa}
            onChange={handleFieldChange("targa")}
            sx={{ ...textFieldSx, mb: fieldSpacing }}
          />

          <Stack direction="row" spacing={2} mb={fieldSpacing}>
            <TextField
              fullWidth
              label="Marca"
              placeholder="Es. Volkswagen"
              value={formData.marca}
              onChange={handleFieldChange("marca")}
              error={Boolean(errors.marca)}
              helperText={errors.marca || " "}
              sx={textFieldSx}
            />
            <TextField
              fullWidth
              label="Modello"
              placeholder="Es. Golf 8"
              value={formData.modello}
              onChange={handleFieldChange("modello")}
              error={Boolean(errors.modello)}
              helperText={errors.modello || " "}
              sx={textFieldSx}
            />
          </Stack>

          <Typography fontWeight={800} mb={1.2} color={ui.headingText}>
            <DirectionsCar sx={{ mr: 1, verticalAlign: "middle", fontSize: 20, color: ui.mutedText }} />
            Provincia di residenza <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            select
            value={formData.provincia}
            onChange={handleFieldChange("provincia")}
            SelectProps={{ MenuProps: provinceMenuProps }}
            error={Boolean(errors.provincia)}
            helperText={errors.provincia || " "}
            placeholder="Seleziona provincia"
            sx={{ ...textFieldSx, mb: fieldSpacing }}
          >
            <MenuItem value="">Seleziona provincia</MenuItem>
            {provinceOptions.map((province) => (
              <MenuItem key={province} value={province}>
                {province}
              </MenuItem>
            ))}
          </TextField>

          <Typography fontWeight={800} mb={1.2} color={ui.headingText}>
            <Euro sx={{ mr: 1, verticalAlign: "middle", fontSize: 20, color: ui.mutedText }} />
            Valore veicolo <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            placeholder="25000"
            value={formData.valoreVeicolo}
            onChange={handleFieldChange("valoreVeicolo")}
            error={Boolean(errors.valoreVeicolo)}
            helperText={errors.valoreVeicolo || " "}
            sx={{ ...textFieldSx, mb: fieldSpacing }}
          />
          <Typography color={ui.mutedText} mb={fieldSpacing}>
            Inserisci il valore di acquisto o attuale del veicolo
          </Typography>

          <Typography fontWeight={800} mb={1.8} color={ui.headingText}>
            <CalendarMonth sx={{ mr: 1, verticalAlign: "middle", fontSize: 20, color: ui.mutedText }} />
            Durata copertura <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <Stack direction="row" spacing={1.35} mb={4}>
            {durations.map((duration) => {
              const isActive = duration === formData.durata;
              return (
                <Box
                  key={duration}
                  onClick={() => handleDurationSelect(duration)}
                  sx={{
                    minWidth: 78,
                    py: 1.1,
                    textAlign: "center",
                    borderRadius: 2,
                    fontWeight: 800,
                    cursor: "pointer",
                    bgcolor: isActive ? "primary.main" : ui.chipInactiveBg,
                    color: isActive ? "white" : ui.mutedText,
                    boxShadow: isActive ? `0 8px 18px ${alpha(theme.palette.primary.main, 0.35)}` : "none",
                  }}
                >
                  {duration}
                  <br />
                  mesi
                </Box>
              );
            })}
          </Stack>
          {errors.durata && (
            <Typography color="error.main" sx={{ mt: -2, mb: 2 }}>
              {errors.durata}
            </Typography>
          )}

          <Box sx={{ borderTop: `1px dashed ${alpha(theme.palette.divider, isDark ? 0.45 : 0.9)}`, pt: 3.5 }}>
            <Typography fontWeight={800} mb={1.2} color={ui.headingText}>
              <PersonOutline sx={{ mr: 1, verticalAlign: "middle", fontSize: 20, color: ui.mutedText }} />
              Intestatario <Typography component="span" color={ui.mutedText}>(facoltativo)</Typography>
            </Typography>
            <Stack direction="row" spacing={2} mb={1.2}>
              <TextField
                fullWidth
                placeholder="Nome"
                value={formData.nome}
                onChange={handleFieldChange("nome")}
                sx={textFieldSx}
              />
              <TextField
                fullWidth
                placeholder="Cognome"
                value={formData.cognome}
                onChange={handleFieldChange("cognome")}
                sx={textFieldSx}
              />
            </Stack>
            {errors.intestatario && (
              <Typography color="error.main" mb={1}>
                {errors.intestatario}
              </Typography>
            )}
            <Typography color={ui.mutedText} mb={3.5}>
              Lascia vuoto per un preventivo anonimo
            </Typography>

            <Button
              type="submit"
              fullWidth
              size="large"
              startIcon={<Search />}
              sx={{
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 800,
                fontSize: 34 / 2,
                color: "white",
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                boxShadow: `0 14px 26px ${alpha(theme.palette.primary.main, 0.35)}`,
              }}
            >
              Calcola Preventivi
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default DealerQuoteForm;
